import iconPng from "@/assets/images/logos/icon.png";
import logoPng from "@/assets/images/logos/logo.png";
import AuditStatusModal from "@/components/modals/AuditStatusModal";
import ContactInfoModal from "@/components/modals/ContactInfoModal";
import FeedbackModal from "@/components/modals/FeedbackModal";
import LoginModal from "@/components/modals/LoginModal";
import NewContactUs from "@/components/modals/NewContactUs";
import { thirdPartyConfig } from "@/config/env";
import { useHeaderNav } from "@/hooks/useHeaderNav";
import { useScrollLock } from "@/hooks/useScrollLock";
import { useAuthStore } from "@/stores/useAuthStore";
import {
  CheckOutlined,
  DownOutlined,
  GlobalOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Dropdown, Grid, Layout, MenuProps } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const { Header: AntHeader } = Layout;

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const {
    active,
    selectAi,
    selectContact,
    selectFeedback,
    contactOpen,
    closeContact,
    setContactOpen,
    loginPromptOpen,
    setLoginPromptOpen,
    loginOpen,
    setLoginOpen,
    feedbackOpen,
    setFeedbackOpen,
    closeFeedback,
    auditStatusModalOpen,
    setAuditStatusModalOpen,
    auditStatus,
  } = useHeaderNav();
  const { token, user, setToken, setUser, logout } = useAuthStore();
  const { t, i18n } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.lg;
  const isMobile = !screens.md;

  // Custom breakpoint for 750px
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // 防止移动端滚动穿透
  useScrollLock(mobileMenuOpen);
  // 防止内测联盟页面覆盖层打开时底层页面滚动（解决双滚动条问题）
  useScrollLock(contactOpen);

  const currentLang = i18n.language.startsWith("en") ? "English" : "简体中文";

  const changeLanguage = (lng: string) => {
    window.location.reload();
    setTimeout(() => {
      i18n.changeLanguage(lng);
    }, 0);
  };

  const langItems: MenuProps["items"] = [
    {
      key: "zh",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: i18n.language.startsWith("zh") ? "#21C4B8" : "#374151",
            fontWeight: i18n.language.startsWith("zh") ? 600 : 400,
          }}
        >
          <span>简体中文</span>
          {i18n.language.startsWith("zh") && (
            <CheckOutlined style={{ color: "#21C4B8", marginLeft: 8 }} />
          )}
        </div>
      ),
      onClick: () => changeLanguage("zh"),
    },
    {
      key: "en",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: i18n.language.startsWith("en") ? "#21C4B8" : "#374151",
            fontWeight: i18n.language.startsWith("en") ? 600 : 400,
          }}
        >
          <span>English</span>
          {i18n.language.startsWith("en") && (
            <CheckOutlined style={{ color: "#21C4B8", marginLeft: 8 }} />
          )}
        </div>
      ),
      onClick: () => changeLanguage("en"),
    },
  ];

  const [loginSource, setLoginSource] = React.useState<
    "contact" | "feedback" | "beta" | undefined
  >(undefined);
  const [contactInfoOpen, setContactInfoOpen] = React.useState(false);

  // 添加调试信息
  // React.useEffect(() => {
  //   console.log('[Header] user value:', user);
  //   console.log('[Header] token value:', token);
  // }, [user, token]);

  // 监听全局下载事件
  React.useEffect(() => {
    let isProcessing = false;

    const handleGlobalSelectContact = () => {
      if (isProcessing) {
        console.log("[Header] 忽略重复的 globalSelectContact 事件");
        return;
      }

      isProcessing = true;
      console.log("[Header] 收到 globalSelectContact 事件");
      selectContact();

      // 500ms 后重置，防止重复触发
      setTimeout(() => {
        isProcessing = false;
      }, 500);
    };

    window.addEventListener("globalSelectContact", handleGlobalSelectContact);
    return () => {
      window.removeEventListener(
        "globalSelectContact",
        handleGlobalSelectContact
      );
    };
  }, [selectContact]);

  // 监听意见反馈事件
  React.useEffect(() => {
    const handleFeedbackOpen = () => {
      console.log("[Header] 收到 feedback:open 事件");
      setFeedbackOpen(true);
    };

    window.addEventListener("feedback:open", handleFeedbackOpen);
    return () => {
      window.removeEventListener("feedback:open", handleFeedbackOpen);
    };
  }, []);

  // 监听联系我们事件
  React.useEffect(() => {
    const handleContactUsOpen = () => {
      console.log("[Header] 收到 contact-us:open 事件");
      setContactOpen(true);
    };

    window.addEventListener("contact-us:open", handleContactUsOpen);
    return () => {
      window.removeEventListener("contact-us:open", handleContactUsOpen);
    };
  }, []);

  // 监听登录事件
  React.useEffect(() => {
    const handleLoginOpen = () => {
      console.log("[Header] 收到 login:open 事件");
      setLoginOpen(true);
    };

    window.addEventListener("globalSelectLogin", handleLoginOpen);
    return () => {
      window.removeEventListener("globalSelectLogin", handleLoginOpen);
    };
  }, []);

  // 监听登录来源变化
  React.useEffect(() => {
    const source = localStorage.getItem("loginSource") as
      | "contact"
      | "feedback"
      | null;
    if (source) {
      setLoginSource(source);
      // 注意：不清除localStorage中的来源标记，因为在登录成功后还需要使用
      // localStorage.removeItem('loginSource')
    }
  }, [loginPromptOpen]);

  const maskedMobile = React.useMemo(() => {
    const p = user?.phone || "";
    // 改进脱敏逻辑，处理各种情况
    if (!p) return "";
    if (p.length < 7) return p; // 太短的号码不脱敏
    if (p.length === 11) {
      // 标准11位手机号
      return `${p.slice(0, 3)}****${p.slice(-4)}`;
    } else {
      // 其他长度的号码，至少保留前后各2位
      const start = Math.min(2, Math.floor(p.length / 2));
      const end = Math.min(2, Math.floor(p.length / 2));
      if (p.length <= start + end) return p;
      return `${p.slice(0, start)}****${p.slice(-end)}`;
    }
  }, [user?.phone]);
  console.log(user);
  const items: MenuProps["items"] = [
    {
      key: "logout",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderRadius: 6,
            color: "#dc2626",
            fontWeight: 500,
            transition: "all 0.2s ease-in-out",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#fef2f2";
            e.currentTarget.style.color = "#b91c1c";
            e.currentTarget.style.transform = "translateX(2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#dc2626";
            e.currentTarget.style.transform = "translateX(0)";
          }}
        >
          <span>{t("navbar.action_logout")}</span>
        </div>
      ),
      onClick: () => {
        logout();
        localStorage.removeItem("token");
        // 登出时关闭内测联盟页面
        setContactOpen(false);
      },
    },
  ];
  return (
    <AntHeader
      className="fixed top-0 left-0 right-0 border-b border-gray-200 pr-0 pl-0"
      style={{
        paddingLeft: 0,
        paddingRight: 0,
        background: "#ffffff",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        zIndex: 1000,
        height: "auto", // 去除默认高度
        lineHeight: "normal", // 重置行高
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          // 统一使用非简写属性，避免与简写属性冲突
          paddingTop: isDesktop ? 15 : isMobile ? 8 : 8,
          paddingBottom: isDesktop ? 15 : isMobile ? 8 : 8,
          paddingLeft: isDesktop ? 24 : isMobile ? 8 : 12,
          paddingRight: isDesktop ? 24 : isMobile ? 8 : 16,
          transform: "translateZ(0)",
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* Mobile View (shown only <= 750px) */}
        <div className="hidden max-[750px]:flex items-center justify-between w-full relative">
          <div style={{ display: "flex", alignItems: "center" }}>
            <MenuOutlined
              className="header-menu-icon"
              style={{ fontSize: 30 }}
              onClick={() => setMobileMenuOpen(true)}
            />
          </div>

          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={logoPng}
              alt="logo"
              style={{ height: 30 }}
              onClick={() => {
                navigate("/");
                selectAi();
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            {!token ? (
              <button
                type="button"
                className="header-login-button"
                onClick={() => navigate("/auth/login")}
              >
                {t("navbar.btn_login")}
              </button>
            ) : (
              <Dropdown
                menu={{ items }}
                placement="bottomRight"
                trigger={["click"]}
              >
                <img
                  src={iconPng}
                  alt="icon"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                />
              </Dropdown>
            )}
          </div>
        </div>

        {/* Desktop View (hidden <= 750px) */}
        <div className="flex max-[750px]:hidden items-center w-full">
          {/* 左侧 LOGO */}
          <div
            className="flex items-center"
            style={{
              justifyContent: isMobile ? "flex-start" : "center",
              marginRight: isMobile ? 0 : 24,
            }}
          >
            <button
              type="button"
              aria-label="返回首页"
              onClick={() => {
                navigate("/");
                // 重置导航状态为AI指数
                selectAi();
              }}
              style={{
                padding: 0,
                margin: 0,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                lineHeight: "normal",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={logoPng}
                alt="logo"
                style={{
                  width: isMobile ? 110 : "clamp(112px, 14vw, 168px)",
                  display: "block",
                  marginLeft: 0,
                }}
                className="h-auto"
              />
            </button>
          </div>

          {/* 中间：PC 显示两个按钮；移动端合并为下拉 */}
          <div
            style={{
              flex: isMobile ? "0 0 33.33%" : 1,
              width: isMobile ? "33.33%" : undefined,
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-start",
              marginLeft: isMobile ? 0 : 12,
              marginTop: 0,
            }}
          >
            {isMobile ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "ai",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "6px 8px",
                            borderRadius: 6,
                            backgroundColor: "transparent",
                            color: active === "ai" ? "#21C4B8" : "#111827",
                            fontWeight: active === "ai" ? 700 : 500,
                            ...(active === "ai"
                              ? {
                                  backgroundImage:
                                    "linear-gradient(135deg, #21C4B8 0%, #4BA67A 100%)",
                                  WebkitBackgroundClip: "text",
                                  backgroundClip: "text",
                                  color: "transparent",
                                }
                              : {}),
                          }}
                        >
                          <span>{t("navbar.menu_brand_index")}</span>
                          {active === "ai" && (
                            <CheckOutlined style={{ color: "#4BA67A" }} />
                          )}
                        </div>
                      ),
                      onClick: () => selectAi(),
                    },
                    {
                      key: "contact",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "6px 8px",
                            borderRadius: 6,
                            backgroundColor: "transparent",
                            color: active === "about" ? "#21C4B8" : "#111827",
                            fontWeight: active === "about" ? 700 : 500,
                            ...(active === "about"
                              ? {
                                  backgroundImage:
                                    "linear-gradient(135deg, #21C4B8 0%, #4BA67A 100%)",
                                  WebkitBackgroundClip: "text",
                                  backgroundClip: "text",
                                  color: "transparent",
                                }
                              : {}),
                          }}
                        >
                          <span>{t("navbar.menu_join")}</span>
                          {active === "about" && (
                            <CheckOutlined style={{ color: "#4BA67A" }} />
                          )}
                        </div>
                      ),
                      onClick: () => selectContact(),
                    },
                    {
                      key: "feedback",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "6px 8px",
                            borderRadius: 6,
                            backgroundColor: "transparent",
                            color:
                              active === "feedback" ? "#21C4B8" : "#111827",
                            fontWeight: active === "feedback" ? 700 : 500,
                            ...(active === "feedback"
                              ? {
                                  backgroundImage:
                                    "linear-gradient(135deg, #21C4B8 0%, #4BA67A 100%)",
                                  WebkitBackgroundClip: "text",
                                  backgroundClip: "text",
                                  color: "transparent",
                                }
                              : {}),
                          }}
                        >
                          <span>{t("navbar.menu_feedback")}</span>
                          {active === "feedback" && (
                            <CheckOutlined style={{ color: "#4BA67A" }} />
                          )}
                        </div>
                      ),
                      onClick: () => {
                        if (!token) {
                          // 未登录时跳转到登录页
                          localStorage.setItem("loginSource", "feedback");
                          navigate("/auth/login");
                        } else {
                          // 已登录时打开反馈模态框
                          selectFeedback();
                        }
                      },
                    },
                    {
                      key: "about-us",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "6px 8px",
                            borderRadius: 6,
                            backgroundColor: "transparent",
                            color: "#111827",
                            fontWeight: 500,
                          }}
                        >
                          <span>{t("navbar.menu_about")}</span>
                        </div>
                      ),
                      onClick: () =>
                        window.open(thirdPartyConfig.websiteLink, "_blank"),
                    },
                    {
                      key: "contact-info",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "6px 8px",
                            borderRadius: 6,
                            backgroundColor: "transparent",
                            color: "#111827",
                            fontWeight: 500,
                          }}
                        >
                          <span>联系我们</span>
                        </div>
                      ),
                      onClick: () => setContactInfoOpen(true),
                    },
                  ],
                }}
              >
                <button
                  type="button"
                  className={`inline-flex items-center cursor-pointer select-none outline-none border-0`}
                  style={{
                    fontSize: "14px",
                    color: "#000000",
                    backgroundColor: "transparent",
                    border: "none",
                    borderRadius: 4,
                    boxShadow: "none",
                    transform: "none",
                  }}
                  aria-haspopup="menu"
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontWeight: 400 }}>
                      {t("navbar.menu_brand_index")}
                    </span>
                    <DownOutlined style={{ fontSize: 12, color: "#666666" }} />
                  </span>
                </button>
              </Dropdown>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <button
                  type="button"
                  aria-pressed={active === "ai"}
                  onClick={selectAi}
                  className={`inline-flex items-center cursor-pointer select-none bg-transparent px-0 py-0 outline-none border-0 ${
                    active === "ai"
                      ? "text-gray-900"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                  style={{
                    fontSize: "18px",
                    padding: "6px 10px",
                    color: active === "ai" ? "#21C4B8" : "#374151",
                  }}
                >
                  <span
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    {t("navbar.menu_brand_index")}
                    {active === "ai" && (
                      <span
                        style={{
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                          bottom: -10,
                          height: 2,
                          width: "120%",
                          backgroundColor: "#21C4B8",
                          borderRadius: 6,
                        }}
                      />
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  aria-pressed={active === "about"}
                  onClick={selectContact}
                  className={`inline-flex items-center cursor-pointer select-none bg-transparent px-0 py-0 outline-none border-0 ${
                    active === "about"
                      ? "text-gray-900"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                  style={{
                    fontSize: "18px",
                    padding: "6px 10px",
                    color: active === "about" ? "#21C4B8" : "#374151",
                  }}
                >
                  <span
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    {t("navbar.menu_join")}
                    {active === "about" && (
                      <span
                        style={{
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                          bottom: -10,
                          height: 2,
                          width: "120%",
                          backgroundColor: "#21C4B8",
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  aria-pressed={active === "feedback"}
                  onClick={() => {
                    if (!token) {
                      // 未登录时跳转到登录页
                      localStorage.setItem("loginSource", "feedback");
                      navigate("/auth/login");
                    } else {
                      // 已登录时打开反馈模态框
                      selectFeedback();
                    }
                  }}
                  className={`inline-flex items-center cursor-pointer select-none bg-transparent px-0 py-0 outline-none border-0 ${
                    active === "feedback"
                      ? "text-gray-900"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                  style={{
                    fontSize: "18px",
                    padding: "6px 10px",
                    color: active === "feedback" ? "#21C4B8" : "#374151",
                  }}
                >
                  <span
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    {t("navbar.menu_feedback")}
                    {active === "feedback" && (
                      <span
                        style={{
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                          bottom: -10,
                          height: 2,
                          width: "120%",
                          backgroundColor: "#21C4B8",
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    window.open(thirdPartyConfig.websiteLink, "_blank")
                  }
                  className="inline-flex items-center cursor-pointer select-none bg-transparent px-0 py-0 outline-none border-0 text-gray-700 hover:text-gray-900"
                  style={{
                    fontSize: "18px",
                    padding: "6px 10px",
                    color: "#374151",
                  }}
                >
                  <span
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    {t("navbar.menu_about")}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setContactInfoOpen(true)}
                  className="inline-flex items-center cursor-pointer select-none bg-transparent px-0 py-0 outline-none border-0 text-gray-700 hover:text-gray-900"
                  style={{
                    fontSize: "18px",
                    padding: "6px 10px",
                    color: "#374151",
                  }}
                >
                  <span
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    {t("navbar.menu_contact")}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* 右侧“加入内测联盟”已收纳到中间合并按钮 */}

          {/* 右侧占位取消：中间按钮已负责居中 */}

          {/* 最右：登录/用户 */}
          {/* Language Switcher */}
          <div
            style={{ marginRight: 12, display: "flex", alignItems: "center" }}
          >
            <Dropdown menu={{ items: langItems }} placement="bottomRight" arrow>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }}
              >
                <GlobalOutlined
                  style={{ fontSize: 16, color: "#374151", marginRight: 6 }}
                />
                <span
                  style={{ color: "#374151", fontSize: 14, fontWeight: 400 }}
                >
                  {currentLang}
                </span>
                <DownOutlined
                  style={{ fontSize: 10, color: "#6b7280", marginLeft: 6 }}
                />
              </div>
            </Dropdown>
          </div>

          {/* 分隔线 */}
          <div
            style={{
              width: "1px",
              height: "20px",
              backgroundColor: "#e5e7eb",
              marginRight: 12,
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              flexShrink: 1,
              minWidth: 0,
              marginTop: 0,
              alignSelf: "center",
            }}
          >
            {!token ? (
              <Button
                size="large"
                type="primary"
                className="header-login-button"
                onClick={() => navigate("/auth/login")}
              >
                {t("navbar.btn_login")}
              </Button>
            ) : (
              <Dropdown
                menu={{ items }}
                placement="bottomRight"
                trigger={["click"]}
                popupRender={(menu) => (
                  <div
                    style={{
                      marginLeft: "-100px",
                    }}
                  >
                    {menu}
                  </div>
                )}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                    borderRadius: 6,
                    transition: "all 0.2s ease-in-out",
                    padding: isMobile ? "6px 0px" : "0px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <img
                    src={iconPng}
                    alt="icon"
                    style={{
                      width: isDesktop ? 48 : 24,
                      height: isDesktop ? 48 : 24,
                      borderRadius: "50%",
                    }}
                  />
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#21C4B8",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                      fontSize: "14px",
                      maxWidth: "120px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {maskedMobile || user?.email || t("navbar.default_user")}
                  </span>
                </div>
              </Dropdown>
            )}
          </div>
        </div>
      </div>

      <Drawer
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width="80%"
        closable={false}
        styles={{ body: { padding: 0 } }}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <img src={logoPng} alt="logo" style={{ height: 30 }} />
            <div style={{ display: "flex", alignItems: "center" }}>
              <Dropdown
                menu={{ items: langItems }}
                placement="bottomRight"
                trigger={["click"]}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                    color: "#666",
                    border: "1px solid #e5e7eb",
                    borderRadius: 4,
                    padding: "4px 8px",
                    cursor: "pointer",
                  }}
                >
                  <GlobalOutlined />
                  <span>{currentLang}</span>
                  <DownOutlined style={{ fontSize: 10 }} />
                </div>
              </Dropdown>
            </div>
          </div>
        }
      >
        <div>
          {[
            {
              label: t("navbar.menu_brand_index"),
              key: "ai",
              onClick: () => {
                selectAi();
                setMobileMenuOpen(false);
              },
            },
            {
              label: t("navbar.menu_join"),
              key: "about",
              onClick: () => {
                selectContact();
                setMobileMenuOpen(false);
              },
            },
            {
              label: t("navbar.menu_feedback"),
              key: "feedback",
              onClick: () => {
                if (!token) {
                  // 未登录时跳转到登录页
                  localStorage.setItem("loginSource", "feedback");
                  navigate("/auth/login");
                } else {
                  // 已登录时打开反馈模态框
                  selectFeedback();
                }
                setMobileMenuOpen(false);
              },
            },
            {
              label: t("navbar.menu_about"),
              key: "about-us",
              onClick: () => {
                window.open(
                  thirdPartyConfig.websiteLink || "https://www.gen-optima.com/",
                  "_blank"
                );
              },
            },
            {
              label: t("navbar.menu_contact"),
              key: "contact-info",
              onClick: () => {
                setContactInfoOpen(true);
                setMobileMenuOpen(false);
              },
            },
          ].map((item) => (
            <div
              key={item.key}
              onClick={item.onClick}
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #f0f0f0",
                fontSize: 16,
                fontWeight: active === item.key ? 600 : 400,
                color: active === item.key ? "#21C4B8" : "#333",
                backgroundColor:
                  active === item.key ? "#e6f7f6" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </Drawer>

      {contactOpen && (
        <div
          style={{
            position: "fixed",
            top: isDesktop ? "79px" : "50px",
            left: 0,
            right: 0,
            bottom: 0,
            background: "#ffffff",
            overflowY: "auto",
            zIndex: 999,
          }}
        >
          {/* <div style={{ position: 'sticky', top: 0, right: 0, padding: 12, display: 'flex', justifyContent: 'flex-end', background: '#ffffff', zIndex: 10 }}>
            <button
              type="button"
              onClick={closeContact}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >关闭</button>
          </div> */}
          <NewContactUs onSubmitSuccess={closeContact} />
        </div>
      )}
      <FeedbackModal open={feedbackOpen} onClose={closeFeedback} />

      <LoginModal
        open={loginOpen || loginPromptOpen}
        onClose={() => {
          setLoginOpen(false);
          setLoginPromptOpen(false);
          // 检查是否来自内测联盟页面的登录
          const source = localStorage.getItem("loginSource");
          if (source !== "contact") {
            // 只有不是来自内测联盟页面的登录才切换到AI指数页面
            selectAi();
          }
          // 清除登录来源标记
          localStorage.removeItem("loginSource");
        }}
        source={loginSource}
        onSubmit={async ({ email }) => {
          // 这里仍调用组件内默认登录逻辑，但同时把简单的用户信息放到 store
          setToken(localStorage.getItem("token") || "token");
          setUser({
            id: "",
            name: email,
            account: email,
            email: email,
            avatar: "",
            status: 1,
            is_beta: 0,
            created_at: "",
            updated_at: "",
            last_login_at: "",
            last_login_ip: "",
            beta_data: undefined,
            mobile: "",
          });
          setLoginPromptOpen(false);
          // 登录后通过全局事件打开对应的模态框（由 LoginModal 内部处理）
        }}
      />
      <ContactInfoModal
        open={contactInfoOpen}
        onClose={() => setContactInfoOpen(false)}
      />
      <AuditStatusModal
        open={auditStatusModalOpen}
        onClose={() => setAuditStatusModalOpen(false)}
        auditStatus={auditStatus}
      />
    </AntHeader>
  );
};

export default Header;
